<script setup lang="ts">
import type { FormContext } from "#bb/composables/useFormField";
import { buildTemplateContext, useTemplating } from "#bb/composables/useTemplating";
import { normalizeAssessmentNavigation } from "#bb/lib/assessmentNavigation";
import { eventBus } from "#bb/lib/eventBus";
import { ResourcePreloadKey } from "#bb/lib/resourcePreload";

const props = defineProps<{ id: string }>();

const { getContentById } = useContentManager();
const definitionData = computed(() => getContentById(props.id) || {});
const configuration = computed(() => normalizeAssessmentNavigation(definitionData.value));

const features = inject("render-features", { editor: false });
const resourcePreload = inject(ResourcePreloadKey, null);
const formContext = inject<FormContext | null>("form-context", null);
const report = inject<any | null>("report", null);
const logger = useLogger("AssessmentNavigation");
const templateContext = computed(() => buildTemplateContext(report));
const { applyTemplate } = useTemplating(templateContext, { logger });

const commands = [
  {
    id: "editors-group",
    type: "group",
    name: "Editors",
    description: "Assessment navigation editor",
    commands: [
      {
        id: "edit",
        name: "Edit",
        description: "Edit the assessment navigation buttons",
        action: async () => {
          eventBus.emit(`component:onEdit:${props.id}`, {
            data: definitionData.value,
          });
        },
        icon: "lucide:edit",
      },
    ],
  },
];

defineExpose({
  commands,
  properties: {
    id: props.id,
    name: "Assessment Navigation",
    icon: "lucide:arrow-left-right",
  },
});

const resolveAction = (key: "back" | "next") => computed(() => {
  const action = configuration.value[key];
  return {
    ...action,
    label: applyTemplate(action.label),
    icon: applyTemplate(action.icon),
    automationPath: applyTemplate(action.automationPath),
    navigationPath: applyTemplate(action.navigationPath),
  };
});

const backAction = resolveAction("back");
const nextAction = resolveAction("next");

const createCompletionAction = (
  action: typeof backAction,
) => {
  let completion: ReturnType<typeof useBlockCompletionAction>;
  completion = useBlockCompletionAction({
    editor: () => features.editor,
    automationPath: () => action.value.automationPath,
    navigationPath: () => action.value.navigationPath,
    navigationDelaySeconds: () => action.value.navigationDelaySeconds,
    beforeExecute: async () => {
      if (action.value.validateForm && formContext) {
        return formContext.validateAll();
      }

      return true;
    },
    beforeNavigation: async () => {
      const target = completion.navigationTarget.value;
      if (
        !action.value.preloadDestination ||
        !resourcePreload ||
        !target?.href ||
        target.external
      ) {
        return;
      }

      const refreshDependencies = action.value.automationPath.trim().length > 0;
      if (!refreshDependencies && resourcePreload.isReady(target.href)) {
        return;
      }

      await resourcePreload.preload(target.href, { refreshDependencies });
    },
  });
  return completion;
};

const backCompletion = createCompletionAction(backAction);
const nextCompletion = createCompletionAction(nextAction);
const busy = computed(() => backCompletion.busy.value || nextCompletion.busy.value);

const watchPreload = (
  action: typeof backAction,
  completion: typeof backCompletion,
) => watch(
  [
    () => features.editor,
    () => action.value.preloadDestination,
    () => completion.navigationTarget.value?.href,
    () => completion.navigationTarget.value?.external,
  ],
  ([editor, enabled, href, external]) => {
    if (
      !import.meta.client ||
      editor ||
      !enabled ||
      !href ||
      external ||
      !resourcePreload ||
      resourcePreload.isReady(href)
    ) {
      return;
    }

    void resourcePreload.preload(href);
  },
  { immediate: true },
);

watchPreload(backAction, backCompletion);
watchPreload(nextAction, nextCompletion);

const actions = computed(() => [
  {
    key: "back" as const,
    action: backAction.value,
    busy: backCompletion.busy.value,
    execute: backCompletion.execute,
  },
  {
    key: "next" as const,
    action: nextAction.value,
    busy: nextCompletion.busy.value,
    execute: nextCompletion.execute,
  },
]);

const alignmentClass = computed(() => {
  switch (configuration.value.alignment) {
    case "start":
      return "justify-start";
    case "center":
      return "justify-center";
    case "end":
      return "justify-end";
    case "between":
    default:
      return "justify-between";
  }
});

const actionWrapperClass = (key: "back" | "next") =>
  configuration.value.alignment === "between"
    ? key === "back"
      ? "flex flex-1 justify-start"
      : "flex flex-1 justify-end"
    : "flex";
</script>

<template>
  <div
    :class="[
      'flex w-full items-center gap-3',
      alignmentClass,
      $attrs.class ?? '',
    ]"
  >
    <template v-for="item in actions" :key="item.key">
      <div
        v-if="item.action.visible || features.editor"
        :class="actionWrapperClass(item.key)"
      >
        <Button
          type="button"
          :variant="item.action.variant"
          :size="configuration.size"
          :disabled="busy || !item.action.enabled || (!item.action.visible && features.editor)"
          :class="{ 'opacity-50': !item.action.visible && features.editor }"
          @click="item.execute"
        >
          <Icon
            v-if="item.busy"
            name="lucide:loader-circle"
            mode="svg"
            class="animate-spin"
          />
          <template v-else>
            <Icon
              v-if="item.action.icon && item.action.iconPosition === 'left'"
              :name="item.action.icon"
              mode="svg"
            />
            <span>{{ item.action.label }}</span>
            <Icon
              v-if="item.action.icon && item.action.iconPosition === 'right'"
              :name="item.action.icon"
              mode="svg"
            />
          </template>
        </Button>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";

export default {
  componentId: ComponentDefinitionIds.ASSESSMENT_NAVIGATION,
  editor: () => import("#bb/components/editor/assessment/navigation.vue"),
  icon: "lucide:arrow-left-right",
  type: "Assessment Navigation",
  description:
    "Configurable Back and Next buttons for multi-page assessments, with optional validation, automations, and redirects.",
  default: {
    alignment: "between",
    size: "default",
    back: {
      visible: true,
      enabled: true,
      label: "Back",
      icon: "lucide:arrow-left",
      iconPosition: "left",
      variant: "outline",
      validateForm: false,
      automationPath: "",
      navigationPath: "",
      navigationDelaySeconds: 0,
      preloadDestination: false,
    },
    next: {
      visible: true,
      enabled: true,
      label: "Next",
      icon: "lucide:arrow-right",
      iconPosition: "right",
      variant: "default",
      validateForm: true,
      automationPath: "",
      navigationPath: "",
      navigationDelaySeconds: 0,
      preloadDestination: false,
    },
  },
  categories: ["Assessment", "Navigation"],
  order: 10052,
};
</script>
